/**
 * QuestTracker (Sirus 3.3.5a) — R232q22 Phase 3.
 *
 * Right-edge sidebar listing every quest the player has pinned via the
 * Журнал заданий «Отслеживать» button. Mirrors the WoW 3.3.5a / Sirus
 * watch frame 1:1 (see `Screenshot_15.png`):
 *
 *   1) Грязная работа              ← gold Cinzel title, numbered medallion
 *      0/7 Гнилых когтей            ← objective row, gold count, muted text
 *   2) Разыскивается: Червеглаз
 *      0/1 Лапа Червеглаза
 *   ...
 *
 *   • position: fixed, top: 230px, right: 16px (sits below the Minimap)
 *   • z-index 25 — above world but below tooltips/modals
 *   • max-height 50vh, scrollable when many quests pinned
 *   • selected/active quest gets a gold left-border bar + soft gold background
 *   • hidden entirely when no quest is tracked
 *
 * Behaviour:
 *   – left-click on a quest title → opens the full Журнал at that quest
 *     (`aiwari-open-quest-log` event with `{ questId }`).
 *   – left-click on an objective row → emits `aiwari-quest-ping`
 *     `{ x, z }` so the minimap pings the objective coords.
 *   – right-click on a quest title → «Перестать отслеживать».
 *   – header collapse arrow folds the body, leaving only the header strip.
 *   – per-quest collapse chevron folds individual quest objectives.
 *
 * Live updates: subscribes to the same `aiwari-quest-state:accepted`
 * localStorage key the legacy QuestLog writes to. Listens for the same
 * objective-tick events (`aiwari-mob-killed`, `aiwari-item-pickup`, etc.)
 * to refresh progress without re-mounting.
 */

import { useEffect, useState } from 'react';
import { QUESTS, type Quest } from '../../data/quests.js';
import { useQuestTracker } from '../../state/quest-tracker-store.js';

const ACCEPTED_KEY = 'aiwari-quest-state:accepted';

interface QuestProgress { questId: string; progress: number; acceptedAt: number; }

function loadAccepted(): QuestProgress[] {
  try { return JSON.parse(localStorage.getItem(ACCEPTED_KEY) ?? '[]') as QuestProgress[]; } catch { return []; }
}

function requiredCount(q: Quest): number { return q.objective.count ?? 1; }

/** Sirus-style objective phrasing — short noun describing the target.
 *  Examples (matches Screenshot_15):
 *    «0/7 Гнилых когтей»
 *    «0/1 Лапа Червеглаза»
 *    «0/10 Перстень Алого ордена»
 *    «0/20 Воин Алого ордена — убито»  (kill quests get suffix)
 */
function objectiveLabel(q: Quest): string {
  const tgt = q.objective.target ?? 'цель';
  if (q.type === 'kill') return `${tgt} — убито`;
  if (q.type === 'gather') return tgt;
  if (q.type === 'deliver') return `Доставить: ${tgt}`;
  if (q.type === 'visit') return 'Достичь места';
  return 'Цель';
}

export function QuestTracker() {
  const tracked = useQuestTracker((s) => s.tracked);
  const untrack = useQuestTracker((s) => s.untrack);
  const [accepted, setAccepted] = useState<QuestProgress[]>(() => loadAccepted());
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedQuests, setCollapsedQuests] = useState<Set<string>>(() => new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Re-poll progress on every tick event the legacy QuestLog reacts to,
  // so the tracker reflects kill/gather counters in near-real-time.
  useEffect(() => {
    const refresh = () => setAccepted(loadAccepted());
    const events = [
      'aiwari-mob-killed',
      'aiwari-item-pickup',
      'aiwari-quest-visit-tick',
      'aiwari-quest-deliver',
      'aiwari-accept-quest',
      'storage',
    ];
    for (const ev of events) window.addEventListener(ev, refresh as EventListener);
    // 5-second polling fallback covers cases where the legacy log mutates
    // localStorage without firing an event (e.g. abandon).
    const id = setInterval(refresh, 5000);
    return () => {
      for (const ev of events) window.removeEventListener(ev, refresh as EventListener);
      clearInterval(id);
    };
  }, []);

  const trackedIds = [...tracked];
  const rows = trackedIds
    .map((id) => {
      const quest = QUESTS.find((q) => q.id === id);
      if (!quest) return null;
      const progress = accepted.find((a) => a.questId === id)?.progress ?? 0;
      return { quest, progress };
    })
    .filter((r): r is { quest: Quest; progress: number } => r !== null);

  if (rows.length === 0) return null;

  const onPing = (q: Quest) => {
    const pos = q.objective.pos ?? q.giverPos;
    if (pos && typeof pos.x === 'number' && typeof pos.z === 'number') {
      window.dispatchEvent(new CustomEvent('aiwari-quest-ping', { detail: { x: pos.x, z: pos.z } }));
    }
  };

  const onTitleClick = (questId: string) => {
    setSelectedId(questId);
    // Open the full Quest Log at this quest.
    window.dispatchEvent(new CustomEvent('aiwari-open-quest-log', { detail: { questId } }));
  };

  const onContextTitle = (e: React.MouseEvent, questId: string) => {
    e.preventDefault();
    untrack(questId);
  };

  const toggleQuest = (e: React.MouseEvent, questId: string) => {
    e.stopPropagation();
    setCollapsedQuests((cur) => {
      const next = new Set(cur);
      if (next.has(questId)) next.delete(questId); else next.add(questId);
      return next;
    });
  };

  return (
    <div className="aiwari-quest-tracker" data-collapsed={collapsed}>
      <header className="qt-header">
        <span className="qt-title">Цели заданий</span>
        <button
          type="button"
          className="qt-collapse"
          aria-label={collapsed ? 'Развернуть' : 'Свернуть'}
          onClick={() => setCollapsed((c) => !c)}
        >
          <span className="qt-arrow" data-collapsed={collapsed}>▾</span>
        </button>
      </header>

      {!collapsed && (
        <ol className="qt-list">
          {rows.map(({ quest, progress }, idx) => {
            const max = requiredCount(quest);
            const done = progress >= max;
            const isSelected = selectedId === quest.id;
            const isQuestCollapsed = collapsedQuests.has(quest.id);
            return (
              <li
                key={quest.id}
                className="qt-item"
                data-selected={isSelected}
                data-done={done}
              >
                <button
                  type="button"
                  className="qt-quest-title"
                  onClick={() => onTitleClick(quest.id)}
                  onContextMenu={(e) => onContextTitle(e, quest.id)}
                  title="ЛКМ — открыть в журнале · ПКМ — перестать отслеживать"
                >
                  <span className="qt-num">{idx + 1})</span>
                  <span className="qt-quest-text">{quest.title.ru}</span>
                  <span
                    className="qt-quest-fold"
                    onClick={(e) => toggleQuest(e, quest.id)}
                    role="button"
                    aria-label={isQuestCollapsed ? 'Развернуть' : 'Свернуть'}
                  >
                    {isQuestCollapsed ? '+' : '−'}
                  </span>
                </button>
                {!isQuestCollapsed && (
                  <button
                    type="button"
                    className="qt-objective"
                    onClick={() => onPing(quest)}
                    data-done={done}
                    title="ЛКМ — указать на карте"
                  >
                    <span className="qt-obj-count">{Math.min(progress, max)}/{max}</span>
                    <span className="qt-obj-text">{objectiveLabel(quest)}</span>
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <style>{`
        .aiwari-quest-tracker {
          position: fixed;
          top: 230px;
          right: 16px;
          width: 230px;
          max-height: 60vh;
          z-index: 25;
          font-family: var(--aiwari-font-body);
          color: var(--aiwari-fg);
          /* Sirus parchment-stone glass — matches .aiwari-modal lite */
          background:
            linear-gradient(180deg, hsl(36 14% 9% / 0.78) 0%, hsl(36 14% 5% / 0.90) 100%),
            radial-gradient(circle at 30% 0%, hsl(44 100% 70% / 0.06), transparent 60%);
          border: 1px solid hsl(44 100% 32%);
          border-radius: 4px;
          box-shadow:
            0 4px 18px hsl(0 0% 0% / 0.6),
            inset 0 0 0 1px hsl(44 100% 49% / 0.18),
            inset 0 1px 0 hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          padding: 6px 8px 8px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          pointer-events: auto;
          user-select: none;
        }
        .aiwari-quest-tracker[data-collapsed='true'] { max-height: none; }

        .aiwari-quest-tracker .qt-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 4px;
          border-bottom: 1px solid hsl(44 100% 49% / 0.28);
          margin-bottom: 6px;
        }
        .aiwari-quest-tracker .qt-title {
          font-family: var(--aiwari-font-caps);
          font-size: 11px;
          letter-spacing: 0.14em;
          color: var(--aiwari-gold-soft);
          text-transform: uppercase;
          text-shadow: 0 1px 0 #000;
        }
        .aiwari-quest-tracker .qt-collapse {
          background: transparent;
          border: none;
          color: var(--aiwari-gold);
          cursor: pointer;
          padding: 0 2px;
          font-size: 12px;
          line-height: 1;
        }
        .aiwari-quest-tracker .qt-arrow {
          display: inline-block;
          transition: transform 120ms linear;
        }
        .aiwari-quest-tracker .qt-arrow[data-collapsed='true'] { transform: rotate(-90deg); }

        .aiwari-quest-tracker .qt-list {
          list-style: none;
          margin: 0;
          padding: 0;
          overflow-y: auto;
          flex: 1;
          min-height: 0;
          counter-reset: qt;
        }
        .aiwari-quest-tracker .qt-item {
          padding: 4px 4px 5px 6px;
          margin-bottom: 4px;
          border-left: 2px solid transparent;
          border-radius: 0 2px 2px 0;
          transition: background-color 80ms linear, border-color 80ms linear;
        }
        .aiwari-quest-tracker .qt-item:last-child { margin-bottom: 0; }
        .aiwari-quest-tracker .qt-item:hover {
          background: hsl(44 100% 49% / 0.06);
        }
        .aiwari-quest-tracker .qt-item[data-selected='true'] {
          background: hsl(44 100% 49% / 0.10);
          border-left-color: var(--aiwari-gold);
          box-shadow: inset 0 0 8px hsl(44 100% 49% / 0.10);
        }

        .aiwari-quest-tracker .qt-quest-title {
          width: 100%;
          display: flex;
          align-items: baseline;
          gap: 4px;
          background: transparent;
          border: none;
          padding: 0 0 2px;
          margin: 0;
          font-family: var(--aiwari-font-display);
          font-size: 13px;
          font-weight: 600;
          color: var(--aiwari-gold);
          letter-spacing: 0.02em;
          line-height: 1.25;
          text-align: left;
          cursor: pointer;
          text-shadow: 0 1px 0 #000;
        }
        .aiwari-quest-tracker .qt-quest-title:hover .qt-quest-text {
          color: var(--aiwari-gold-soft);
          text-shadow: 0 0 6px hsl(44 100% 49% / 0.6);
        }
        .aiwari-quest-tracker .qt-num {
          flex: 0 0 auto;
          color: var(--aiwari-gold-soft);
          font-family: var(--aiwari-font-mono);
          font-size: 11px;
          font-weight: 700;
          padding-right: 1px;
        }
        .aiwari-quest-tracker .qt-quest-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .aiwari-quest-tracker .qt-quest-fold {
          flex: 0 0 auto;
          color: var(--aiwari-gold-dark);
          font-family: var(--aiwari-font-mono);
          font-size: 14px;
          line-height: 1;
          padding: 0 2px;
          opacity: 0.7;
          cursor: pointer;
        }
        .aiwari-quest-tracker .qt-quest-fold:hover {
          color: var(--aiwari-gold);
          opacity: 1;
        }
        .aiwari-quest-tracker .qt-item[data-done='true'] .qt-quest-text {
          color: hsl(120 50% 65%);
          text-decoration: line-through;
        }

        .aiwari-quest-tracker .qt-objective {
          width: 100%;
          display: flex;
          align-items: baseline;
          gap: 6px;
          background: transparent;
          border: none;
          padding: 1px 0 0 14px;
          font-family: var(--aiwari-font-body);
          font-size: 12px;
          color: var(--aiwari-fg);
          cursor: pointer;
          text-align: left;
          line-height: 1.3;
        }
        .aiwari-quest-tracker .qt-objective:hover .qt-obj-text {
          color: var(--aiwari-gold-soft);
        }
        .aiwari-quest-tracker .qt-objective[data-done='true'] {
          color: var(--aiwari-fg-muted);
        }
        .aiwari-quest-tracker .qt-objective[data-done='true'] .qt-obj-count {
          color: hsl(120 50% 65%);
        }
        .aiwari-quest-tracker .qt-obj-count {
          flex: 0 0 auto;
          font-family: var(--aiwari-font-mono);
          font-size: 11px;
          font-weight: 700;
          color: var(--aiwari-gold-soft);
          min-width: 28px;
        }
        .aiwari-quest-tracker .qt-obj-text {
          flex: 1;
          color: var(--aiwari-fg);
          font-style: normal;
        }

        @media (pointer: coarse) {
          .aiwari-quest-tracker {
            top: auto;
            bottom: 96px;
            right: 8px;
            width: 210px;
            max-height: 35vh;
          }
        }
      `}</style>
    </div>
  );
}
